import * as React from "react";

import { ContractFamilyConfigurationModal } from "@/components/modals/ContractFamilyConfigurationModal";
import { Button } from "@/components/ui/button";
import { pluralize } from "@/helpers/pluralize";
import { useCanConfigureContractFamilies } from "@/hooks/use-contract-families-access";
import type { MasterCollectionMismatch } from "@/types/contract-family-pipeline";

interface Props {
  /** Names of the missing settings, as returned by `useContractSummaryReadiness`. */
  missing: string[];
  /** Set when the settings are complete but the Master Collection no longer fits the template. */
  mismatch?: MasterCollectionMismatch | null;
}

const listSettings = (names: string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];

/** Explains which Contract Family settings block a contract summary and lets an administrator fix them in place. */
export const ContractSummarySettingsNotice: React.FC<Props> = ({ missing, mismatch }) => {
  const canConfigureContractFamilies = useCanConfigureContractFamilies();
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);

  return (
    <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      {mismatch ? (
        <>
          <p>
            Contract summaries cannot be created yet: the Master Collection no longer matches the Snapshot Template.
          </p>
          {mismatch.changed_column_names.length > 0 ? (
            <p className="mt-1">Data type changed: {mismatch.changed_column_names.join(", ")}.</p>
          ) : null}
          {mismatch.removed_column_count > 0 ? (
            <p className="mt-1">
              Removed from the template: {pluralize(mismatch.removed_column_count, "column", "columns")}.
            </p>
          ) : null}
          {mismatch.changed_column_names.length === 0 && mismatch.removed_column_count === 0 ? (
            <p className="mt-1">It was created for a different template.</p>
          ) : (
            <p className="mt-1">
              Restore these in the template to continue. Existing rows stay in the Master Collection.
            </p>
          )}
        </>
      ) : (
        <p>
          Contract summaries cannot be created yet: the {listSettings(missing)} {missing.length > 1 ? "are" : "is"} not
          set or no longer available.
        </p>
      )}
      {canConfigureContractFamilies ? (
        <>
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setIsSettingsOpen(true)}>
            Open Contract Family settings
          </Button>
          <ContractFamilyConfigurationModal open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
        </>
      ) : (
        <p className="mt-1">Ask an organization administrator to complete the Contract Family settings.</p>
      )}
    </div>
  );
};
